import { Head, usePage } from '@inertiajs/react';
import { ChevronDown, LoaderCircle } from 'lucide-react';
import { motion } from 'motion/react';
import { useEffect, useRef, useState } from 'react';

import { Container } from '@/theme1/components/common/Container';
import TopWantedHeader from '@/theme1/components/topWanted/TopWantedHeader';
import TopWantedList from '@/theme1/components/topWanted/TopWantedList';

const emptyPagination = {
    page: 1,
    perPage: 10,
    total: 0,
    hasMore: false,
    nextPage: 2,
};

export default function TopWanted() {
    const { topWantedPage } = usePage().props;
    const initialProducts = topWantedPage?.products ?? [];
    const initialPagination = topWantedPage?.pagination ?? emptyPagination;
    const [products, setProducts] = useState(initialProducts);
    const [pagination, setPagination] = useState(initialPagination);
    const [loadingMore, setLoadingMore] = useState(false);
    const sentinelRef = useRef(null);

    useEffect(() => {
        if (!sentinelRef.current || !pagination.hasMore) {
            return undefined;
        }

        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0]?.isIntersecting) {
                    void loadMoreProducts();
                }
            },
            {
                rootMargin: '320px 0px',
            },
        );

        observer.observe(sentinelRef.current);

        return () => {
            observer.disconnect();
        };
    }, [pagination.hasMore, pagination.nextPage, loadingMore]);

    const loadMoreProducts = async () => {
        if (loadingMore || !pagination.hasMore) {
            return;
        }

        setLoadingMore(true);

        try {
            const url = new URL(window.location.href);
            url.searchParams.set('page', String(pagination.nextPage));

            const response = await fetch(url.toString(), {
                headers: {
                    Accept: 'application/json',
                },
            });

            if (!response.ok) {
                throw new Error('Failed to load more top wanted products');
            }

            const payload = await response.json();
            const nextProducts = Array.isArray(payload.products)
                ? payload.products
                : [];

            setProducts((current) => [...current, ...nextProducts]);
            setPagination(payload.pagination ?? emptyPagination);
        } catch (error) {
            // Keep the already loaded products visible if the next batch fails.
            console.error(error);
        } finally {
            setLoadingMore(false);
        }
    };

    const seo = topWantedPage?.seo ?? {
        title: 'Top Wanted - Captex',
        description: 'Most wanted products at Captex.',
    };

    const header = topWantedPage?.header ?? {
        eyebrow: 'Top Wanted',
        title: 'Most wanted right now',
        subtitle: 'Ranked by live sales data.',
        description: 'Scroll to load more products.',
    };

    return (
        <>
            <Head title={seo.title}>
                <meta name="description" content={seo.description} />
            </Head>

            <main className="overflow-hidden">
                <Container className="pb-20 sm:pb-24 lg:pb-32">
                    <TopWantedHeader header={header} />

                    <div className="mx-auto max-w-5xl">
                        <TopWantedList products={products} />

                        <div ref={sentinelRef} className="h-1" />

                        <div className="mt-10 flex items-center justify-center">
                            {loadingMore ? (
                                <div className="inline-flex items-center gap-3 rounded-full border border-border bg-white px-5 py-3 text-sm font-semibold text-muted-foreground shadow-sm">
                                    <LoaderCircle className="size-4 animate-spin" />
                                    Loading more products
                                </div>
                            ) : pagination.hasMore ? (
                                <button
                                    type="button"
                                    onClick={() => void loadMoreProducts()}
                                    className="inline-flex items-center gap-3 rounded-full border border-neutral-950 bg-neutral-950 px-6 py-3 text-sm font-black tracking-wide text-white uppercase transition hover:-translate-y-0.5 hover:bg-neutral-800"
                                >
                                    <ChevronDown className="size-4" />
                                    Load more
                                </button>
                            ) : (
                                <motion.p
                                    initial={{ opacity: 0, y: 10 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true }}
                                    className="rounded-full border border-border bg-white px-5 py-3 text-sm font-semibold text-muted-foreground shadow-sm"
                                >
                                    You have reached the end of the list.
                                </motion.p>
                            )}
                        </div>
                    </div>
                </Container>
            </main>
        </>
    );
}
